<template>
  <div
    ref="tuningControlsRef"
    class="tuning-controls"
    :class="{ bass: numStrings === 4 }"
    @mouseenter="handleTuningHover"
    @mouseleave="handleMouseLeave"
  >
    <div
      v-for="(string, index) in reversedLocalTuning"
      :key="index"
      class="tuning-selector"
      :class="{ 'tuning-selector--open': openDropdownIndex === index }"
      :ref="(el) => setSelectorRef(index, el)"
    >
      <!-- Trigger button - always visible, highlighted when open -->
      <button
        class="tuning-selector-trigger interactive-outline"
        :class="{
          'tuning-selector-trigger--active': openDropdownIndex === index,
          'selector-open': openDropdownIndex === index,
        }"
        :aria-label="`String tuning: ${string}. Click to open tuning selector`"
        tabindex="0"
        @click.stop="openSelector(index)"
        @mousedown.stop
        @keydown="handleTriggerKeydown($event, index)"
        @focus="handleTriggerFocus"
        @blur="handleTriggerBlur"
      >
        <NoteLabel
          :chromatic-index="noteNames.indexOf(string)"
          :display-mode="'logical'"
          :key-signature="rootNote"
          size="small"
        />
      </button>
    </div>

    <!-- Global dropdown - positioned consistently for all strings -->
    <Transition name="tuning-dropdown">
      <div
        v-if="openDropdownIndex !== null"
        ref="dropdownRef"
        class="tuning-selector-open"
        role="listbox"
        :aria-label="'Select tuning note'"
        tabindex="0"
        @keydown="handleDropdownKeydown($event)"
        :style="dropdownStyle"
      >
      <button
        v-for="note in noteNames"
        :key="note"
        class="tuning-option"
        :class="{
          'tuning-option--selected': note === getCurrentSelectedNote(),
          'tuning-option--focused':
            focusedNoteIndex === noteNames.indexOf(note),
        }"
        :aria-label="`${note} key`"
        :aria-selected="note === getCurrentSelectedNote()"
        role="option"
        @click="selectNote(numStrings - 1 - openDropdownIndex, note)"
        @focus="focusedNoteIndex = noteNames.indexOf(note)"
        @keydown="handleOptionKeydown($event, note)"
      >
        <!-- Selection indicator dot -->
        <div
          v-if="note === getCurrentSelectedNote()"
          class="selection-indicator"
        ></div>
        <NoteLabel
          :chromatic-index="noteNames.indexOf(note)"
          :display-mode="'logical'"
          :key-signature="rootNote"
          size="medium"
        />
      </button>
      </div>
    </Transition>

    <!-- Element Tooltip -->
    <ElementTooltip
      :visible="elementTooltip.state.value.visible"
      :content="elementTooltip.state.value.content"
      :target-element="elementTooltip.state.value.targetElement"
      :position="elementTooltip.state.value.position"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { defineProps, defineEmits } from "vue";
import ElementTooltip from "./ElementTooltip.vue";
import NoteLabel from "./NoteLabel.vue";
import { useElementTooltip } from "@/composables/useElementTooltip";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";

const props = defineProps<{
  tuning: string[];
  numStrings: number;
  noteNames: string[];
  isGuitar: boolean;
}>();

// Access fretboard store for root note (key signature)
const store = useFretboardStore();
const { rootNote } = storeToRefs(store);

// Initialize element tooltip system
const elementTooltip = useElementTooltip();

// Global tooltip coordination
const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

const emit = defineEmits<{
  (e: "tuning-changed", newTuning: string[]): void;
}>();

const localTuning = ref([...props.tuning]);
const openDropdownIndex = ref<number | null>(null);
const focusedNoteIndex = ref(0);
const tuningControlsRef = ref<HTMLElement>();
const selectorRefs = ref<Record<number, HTMLElement>>({});
const dropdownRef = ref<HTMLElement>();
const transformOriginY = ref(0);

const reversedLocalTuning = computed(() => [...localTuning.value].reverse());

// Computed style for dropdown positioning and animation
const dropdownStyle = computed(() => {
  if (openDropdownIndex.value === null) return {};

  return {
    transformOrigin: `left ${transformOriginY.value}px`,
  };
});

// Tooltip functions accessed directly from props to maintain reactivity

watch(
  () => props.tuning,
  (newTuning) => {
    localTuning.value = [...newTuning];
  },
  { immediate: true }
);

function changeTuning(index: number, newValue: string) {
  const newTuning = [...localTuning.value];
  newTuning[index] = newValue;
  emit("tuning-changed", newTuning);
}

// Set selector ref for each dropdown
function setSelectorRef(index: number, el: any) {
  if (el) {
    selectorRefs.value[index] = el;
  }
}

function openSelector(index: number) {
  // Toggle behavior: if clicking the same trigger that's already open, close it
  if (openDropdownIndex.value === index) {
    closeAndReturnFocus();
    return;
  }

  // Set consistent transform origin for all dropdowns
  transformOriginY.value = 145; // Center of dropdown height (290px / 2)

  openDropdownIndex.value = index;
  const currentNote = reversedLocalTuning.value[index];
  focusedNoteIndex.value = props.noteNames.indexOf(currentNote);

  // Focus the dropdown container first, then the selected option
  nextTick(() => {
    if (dropdownRef.value) {
      dropdownRef.value.focus();

      // Then focus the selected option
      nextTick(() => {
        const selectedOption = dropdownRef.value?.querySelector(
          ".tuning-option--selected"
        ) as HTMLElement;
        if (selectedOption) {
          selectedOption.focus();
        }
      });
    }
  });
}

function getCurrentSelectedNote() {
  if (openDropdownIndex.value === null) return "";
  return reversedLocalTuning.value[openDropdownIndex.value];
}

function closeSelector() {
  openDropdownIndex.value = null;
  focusedNoteIndex.value = 0;

  // If mouse is still over the tuning controls after closing, show tooltip again
  nextTick(() => {
    if (tuningControlsRef.value?.matches(":hover")) {
      elementTooltip.showAbove("Change tuning", tuningControlsRef.value);
      showGlobalTooltip("tuning-controls");
    }
  });
}

function closeAndReturnFocus() {
  const currentIndex = openDropdownIndex.value;
  closeSelector();

  // Return focus to the trigger button
  nextTick(() => {
    if (currentIndex !== null) {
      const triggerButton = selectorRefs.value[currentIndex]?.querySelector(
        ".tuning-selector-trigger"
      ) as HTMLElement;
      if (triggerButton) {
        triggerButton.focus();
      }
    }
  });
}

function selectNote(tuningIndex: number, note: string) {
  const currentDropdownIndex = openDropdownIndex.value;
  changeTuning(tuningIndex, note);
  closeSelector();

  // Return focus to the trigger button after selection
  nextTick(() => {
    if (currentDropdownIndex !== null) {
      const triggerButton = selectorRefs.value[
        currentDropdownIndex
      ]?.querySelector(".tuning-selector-trigger") as HTMLElement;
      if (triggerButton) {
        triggerButton.focus();
      }
    }
  });
}

function handleTriggerKeydown(event: KeyboardEvent, index: number) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    openSelector(index);
  }
}

function handleDropdownKeydown(event: KeyboardEvent) {
  if (openDropdownIndex.value === null) return;

  switch (event.key) {
    case "Escape":
      event.preventDefault();
      event.stopPropagation();
      closeAndReturnFocus();
      break;

    case "Tab": {
      closeSelector();
      // Don't prevent default - let browser handle tab naturally
      break;
    }

    case "ArrowUp":
      event.preventDefault();
      event.stopPropagation();
      moveFocus(-1);
      break;

    case "ArrowDown":
      event.preventDefault();
      event.stopPropagation();
      moveFocus(1);
      break;

    case "Enter":
    case " ": {
      event.preventDefault();
      event.stopPropagation();
      const focusedNote = props.noteNames[focusedNoteIndex.value];
      if (focusedNote) {
        selectNote(props.numStrings - 1 - openDropdownIndex.value, focusedNote);
      }
      break;
    }
  }
}

function handleOptionKeydown(event: KeyboardEvent, note: string) {
  switch (event.key) {
    case "Enter":
    case " ":
      event.preventDefault();
      event.stopPropagation();
      if (openDropdownIndex.value !== null) {
        selectNote(props.numStrings - 1 - openDropdownIndex.value, note);
      }
      break;
  }
}

function moveFocus(direction: number) {
  const newIndex = Math.max(
    0,
    Math.min(props.noteNames.length - 1, focusedNoteIndex.value + direction)
  );
  focusedNoteIndex.value = newIndex;
  focusNoteAtIndex(newIndex);
}

function focusNoteAtIndex(noteIndex: number) {
  nextTick(() => {
    const noteButton = dropdownRef.value?.querySelector(
      `[aria-label*="${props.noteNames[noteIndex]}"]`
    ) as HTMLElement;
    if (noteButton) {
      noteButton.focus();
    }
  });
}

function handleClickOutside(event: MouseEvent) {
  if (openDropdownIndex.value !== null) {
    const currentSelector = selectorRefs.value[openDropdownIndex.value];
    if (currentSelector && !currentSelector.contains(event.target as Node)) {
      closeSelector();
    }
  }
}

// Tooltip handlers that position above the group
function handleTuningHover(event: MouseEvent) {
  // Only show tooltip if no dropdown is open
  if (openDropdownIndex.value === null) {
    const target = event.currentTarget as HTMLElement;
    elementTooltip.showAbove("Change tuning", target);
    showGlobalTooltip("tuning-controls");
  }
}

function handleMouseLeave() {
  // Only hide tooltip if no dropdown is open
  if (openDropdownIndex.value === null) {
    elementTooltip.hideTooltip();
    hideGlobalTooltip("tuning-controls");
  }
}

function handleTriggerFocus() {
  // Show tooltip when trigger receives focus (keyboard navigation)
  if (openDropdownIndex.value === null && tuningControlsRef.value) {
    // Show tooltip above the entire group, not individual trigger
    elementTooltip.showAbove("Change tuning", tuningControlsRef.value);
    showGlobalTooltip("tuning-controls");
  }
}

function handleTriggerBlur() {
  // Hide tooltip when focus leaves the entire tuning controls group
  setTimeout(() => {
    if (openDropdownIndex.value === null && tuningControlsRef.value) {
      // Only hide if focus has moved outside the tuning controls container
      if (!tuningControlsRef.value.contains(document.activeElement)) {
        elementTooltip.hideTooltip();
        hideGlobalTooltip("tuning-controls");
      }
    }
  }, 100);
}

// Global tooltip hide function
function hideTooltip() {
  elementTooltip.hideTooltip();
}

// Lifecycle hooks for global tooltip registration and click outside handling
onMounted(() => {
  registerTooltip("tuning-controls", hideTooltip);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  unregisterTooltip("tuning-controls");
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.tuning-controls {
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 53px;
  left: 12px;
  gap: 6px;
  border-radius: 48px;

  &:has(.tuning-selector--open) {
    .tuning-selector:not(.tuning-selector--open) {
      opacity: 0.2;
    }
  }
}

.tuning-controls:hover {
  cursor: pointer;
  .tuning-selector-trigger {
    background-color: var(--shade-10);
  }
}

.tuning-controls.bass {
  top: 66px;
  gap: 28px;
}

.tuning-selector {
  position: relative;
  width: 40px; /* Always maintain closed state size */
  height: 40px; /* Always maintain closed state size */
}

.tuning-selector-trigger {
  width: 40px;
  height: 40px;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 20px;
  color: var(--shade-50);
  cursor: pointer;
  transition: all 0.12s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Ensure all child elements don't interfere with click events */
  * {
    pointer-events: none;
  }

  &:focus-visible {
    /* outline: 2px solid var(--light-48); */
    outline-offset: 2px;
  }

  &:hover {
    background-color: var(--shade-10);
    color: var(--shade-70);
    outline: none;
    border: 2px solid var(--shade-50);
  }

  &--active {
    background-color: var(--shade-10);
    border: 2px solid var(--color-0);
    color: var(--shade-70);
    box-shadow: 0 0 0 2px rgba(255, 97, 97, 0.2);

    /* Ensure focus outline is visible when active */
    &:focus-visible {
      /* outline: 2px solid var(--light-48); */
      outline-offset: 2px;
    }
  }

  /* Selector open state - same styling as focus but always visible */
  &.selector-open {
    background-color: var(--shade-10);
    border: 2px solid var(--shade-50);
    color: var(--shade-70);
    /* outline: 2px solid var(--light-48); */
    outline-offset: 2px;
  }
}

.tuning-selector-open {
  position: absolute;
  left: 40px;
  top: -8px;
  height: 286px;
  bottom: 0;
  width: 60px;
  border: 1px solid var(--shade-30);
  background: #141414de;
  border-radius: 8px;
  backdrop-filter: blur(12px);
  padding: 8px 4px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0px;
  z-index: 100000; /* Ensure it appears above everything */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  /* Transitions will be handled by Vue transition classes */

  &:focus-visible {
    /* outline: 2px solid var(--light-24); */
    outline-offset: 2px;
  }
}

.tuning-option {
  width: 100%;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--shade-60);
  cursor: pointer;
  transition: all 0.12s ease-out;
  display: flex;
  align-items: center;
  justify-content: left;
  padding-left: 17px;
  position: relative; /* Make indicator position relative to each button */

  &:focus-visible {
    /* outline: 2px solid var(--light-48); */
    outline-offset: 2px;
  }

  &:hover:not(.tuning-option--selected) {
    background: var(--shade-20);
    color: var(--shade-70);
  }
}

.tuning-option--selected {
  color: var(--shade-70);
}

.tuning-option--focused {
  background: var(--shade-30);
  color: var(--shade-70);
}

.selection-indicator {
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  background-color: var(--shade-70);
  border-radius: 50%;
  pointer-events: none;
}

/* Vue transition classes for dropdown */
.tuning-dropdown-enter-active,
.tuning-dropdown-leave-active {
  transition: all 300ms ease-out;
}

.tuning-dropdown-enter-from,
.tuning-dropdown-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.tuning-dropdown-enter-to,
.tuning-dropdown-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
